import { uniqBy } from 'lodash';
import React, { useState } from 'react';
import { LuChevronDown, LuChevronRight, LuChevronUp, LuCigarette } from 'react-icons/lu';

import { DwhSriCategory, DwhSriSubCategory } from '@ndvr/market-data-api';
import { TradeRestrictionCategoryOverrideRequest, TradeRestrictionCategoryRequest } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { formatCurrencyHuman, formatPercent, useRequiredParams } from 'utils';
import { useSetTradeRestrictionCategoriesAndOverrides } from 'views/portfolio/account-group-queries';
import { categoryIconMap } from 'views/portfolio/PortfolioPage/tabs/equity-preferences/constants';

import { type AffectedStock, AffectedStocksTable } from './AffectedStocksTable';
import { AllRestrictedStocksSection } from './AllRestrictedStocksSection';
import { CategoryOverride, EditSRICategoryModal } from './EditSRICategoryModal';
import { type CategoryStats, useSRIRestrictions } from './useSRIRestrictions';

interface CategoryItemProps {
    category: DwhSriCategory;
    isOpen: boolean;
    onToggle: () => void;
    stats: CategoryStats;
    excludedSubCategories: DwhSriSubCategory[];
    affectedStocks: AffectedStock[];
    onEdit: () => void;
}

export const SRIRestrictionsTab = () => {
    const { accountGroupGUID } = useRequiredParams(['accountGroupGUID']);
    const {
        restrictionsInfo,
        preferences,
        isSubCategoryExcluded,
        getCategoryStats,
        getAffectedStocks,
        getAllRestrictedStocks,
        isStockDataLoading,
    } = useSRIRestrictions(accountGroupGUID);

    const {
        mutate: setTradeRestrictionCategoriesAndOverrides,
        isPending: setTradeRestrictionCategoriesAndOverridesPending,
    } = useSetTradeRestrictionCategoriesAndOverrides(accountGroupGUID);

    const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});
    const [editingCategory, setEditingCategory] = useState<DwhSriCategory | null>(null);

    const allRestrictedStocks = getAllRestrictedStocks();
    const isLoading = restrictionsInfo?.isLoading || isStockDataLoading;

    const toggleCategoryOpen = (categoryId: string) => {
        setOpenCategories((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId],
        }));
    };

    const handleSave = (
        restrictedSubCategories: string[],
        overrides: CategoryOverride[],
        restrictedStocks: CategoryOverride[],
    ) => {
        const allRestrictedSubCategories: string[] = [];
        const allStockOverrides: CategoryOverride[] =
            preferences?.restrictedCategoryOverrides?.map((override) => ({
                shareId: override.shareId,
                shareClassId: override.shareClassId || '',
            })) || [];

        for (const subCategory of Object.keys(restrictionsInfo?.sriRestrictions || {})) {
            if (subCategory !== editingCategory?.sriCategoryId) {
                const restrictionData = restrictionsInfo?.sriRestrictions?.[subCategory];
                if (Array.isArray(restrictionData)) {
                    allRestrictedSubCategories.push(...restrictionData.map((r) => r.subCategoryId));
                }
            }
        }

        allRestrictedSubCategories.push(...restrictedSubCategories);
        allStockOverrides.push(...overrides);
        let uniqueOverrides = uniqBy(allStockOverrides, 'shareId');

        uniqueOverrides = uniqueOverrides.filter((o) => {
            return !restrictedStocks.some((rs) => rs.shareId === o.shareId);
        });

        setTradeRestrictionCategoriesAndOverrides(
            {
                tradeRestrictionCategories: allRestrictedSubCategories.map(
                    (excludedSubCategoryId) =>
                        ({
                            portfolioGUID: accountGroupGUID,
                            sriCategoryId: excludedSubCategoryId,
                        }) as TradeRestrictionCategoryRequest,
                ),
                tradeRestrictionCategoryOverrides: uniqueOverrides.map(
                    (override) =>
                        ({
                            portfolioGUID: accountGroupGUID,
                            shareId: override.shareId,
                            shareClassId: override.shareClassId,
                        }) as TradeRestrictionCategoryOverrideRequest,
                ),
            },
            {
                onSuccess: () => {
                    setEditingCategory(null);
                },
            },
        );
    };

    return (
        <div className="space-y-4">
            <div className="mb-4">
                Apply restrictions to entire categories of securities based on Sustainable, Responsible, and Impact
                investing criteria
            </div>

            <AllRestrictedStocksSection stocks={allRestrictedStocks} isLoading={isLoading} />

            <div className="py-4 text-gray-500">
                <div className="space-y-4">
                    {restrictionsInfo?.categories?.map((category) => {
                        const isOpen = Boolean(openCategories[category.sriCategoryId]);
                        const stats = getCategoryStats(category);
                        const excludedSubCategories = category.subCategories.filter((subCategory: DwhSriSubCategory) =>
                            isSubCategoryExcluded(category.sriCategoryId, subCategory.sriSubCategoryId),
                        );
                        const affectedStocks = getAffectedStocks(category);

                        return (
                            <CategoryItem
                                key={category.sriCategoryId}
                                category={category}
                                isOpen={isOpen}
                                onToggle={() => toggleCategoryOpen(category.sriCategoryId)}
                                stats={stats}
                                excludedSubCategories={excludedSubCategories}
                                affectedStocks={affectedStocks}
                                onEdit={() => setEditingCategory(category)}
                            />
                        );
                    })}
                </div>
            </div>

            {editingCategory && (
                <EditSRICategoryModal
                    isOpen={true}
                    isSaving={setTradeRestrictionCategoriesAndOverridesPending}
                    onClose={() => setEditingCategory(null)}
                    onSave={handleSave}
                    category={editingCategory}
                    excludedSubCategories={editingCategory.subCategories
                        .filter((subCategory: DwhSriSubCategory) =>
                            isSubCategoryExcluded(editingCategory.sriCategoryId, subCategory.sriSubCategoryId),
                        )
                        .map((subCategory: DwhSriSubCategory) => subCategory.sriSubCategoryId)}
                />
            )}
        </div>
    );
};

const CategoryItem = ({
    category,
    isOpen,
    onToggle,
    stats,
    excludedSubCategories,
    affectedStocks,
    onEdit,
}: CategoryItemProps) => {
    const [isTableOpen, setIsTableOpen] = useState(false);
    const IconComponent = categoryIconMap[category.sriCategoryId as keyof typeof categoryIconMap] || LuCigarette;
    const statsText = `${excludedSubCategories.length} restricted Sub-Categories, ${stats.excludedStocks} Stocks Excluded, ${stats.tradableStocks} Tradable`;

    return (
        <Collapsible open={isOpen} onOpenChange={onToggle}>
            <div className="rounded-lg border bg-white">
                <div className="flex items-center justify-between bg-white p-4 transition-colors hover:bg-gray-50">
                    <CollapsibleTrigger className="flex flex-1 items-center gap-4 text-left">
                        <div
                            className="flex h-12 w-12 items-center justify-center rounded-full"
                            style={{ backgroundColor: 'rgba(125, 85, 43, 0.1)' }}
                        >
                            <IconComponent className="h-6 w-6" style={{ color: 'rgb(125, 85, 43)' }} />
                        </div>
                        <div>
                            <h3 className="text-cocoa-1000 font-semibold">{category.sriCategoryLabel}</h3>
                            <p className="text-sm text-gray-500">{statsText}</p>
                        </div>
                    </CollapsibleTrigger>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={onEdit}>
                            Edit
                        </Button>

                        {isOpen ? (
                            <LuChevronDown className="h-5 w-5 text-gray-400" />
                        ) : (
                            <LuChevronRight className="h-5 w-5 text-gray-400" />
                        )}
                    </div>
                </div>

                <CollapsibleContent>
                    <div className="space-y-6 border-t p-6" style={{ backgroundColor: 'rgba(125, 85, 43, 0.1)' }}>
                        <div className="grid grid-cols-2 gap-12">
                            <div>
                                <h4 className="mb-3 text-sm font-medium text-gray-700">Statistics</h4>
                                <div className="space-y-2 text-sm text-gray-600">
                                    <div className="space-y-1">
                                        <div className="flex items-center justify-between pb-1">
                                            <span className="text-gray-700">Total Sub-Categories</span>
                                            <span className="font-medium">{stats.totalCategories}</span>
                                        </div>
                                        <div className="flex items-center justify-between border-b border-gray-300 pb-1">
                                            <span className="text-gray-700">Excluded Sub-Categories</span>
                                            <span className="font-medium">{excludedSubCategories.length}</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Excluded Stocks</span>
                                            <span className="font-medium">{stats.excludedStocks}</span>
                                        </div>
                                        <div className="flex items-center justify-between border-b border-gray-300 py-1">
                                            <span className="text-gray-700">Tradeable Stocks</span>
                                            <span className="font-medium">{stats.tradableStocks}</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Excluded Market Value</span>
                                            <span className="font-medium">
                                                {formatCurrencyHuman(stats.excludedMarketValue, 'USD')}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Percentage of Index</span>
                                            <span className="font-medium">
                                                {formatPercent(stats.excludedMarketShare)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <h4 className="mb-3 text-sm font-medium text-gray-700">Sub-Categories</h4>
                                <div className="space-y-2">
                                    {category.subCategories?.map((subcategory) => (
                                        <div
                                            key={subcategory.sriSubCategoryId}
                                            className="flex items-center justify-between rounded border bg-white px-3 py-2"
                                        >
                                            <span className="text-sm text-gray-700">
                                                {subcategory.sriSubCategoryLabel}
                                            </span>
                                            <span className="text-sm text-gray-700">
                                                {excludedSubCategories.some(
                                                    (excludedSubCategory) =>
                                                        excludedSubCategory.sriSubCategoryId ===
                                                        subcategory.sriSubCategoryId,
                                                ) ? (
                                                    <span className="text-red-600">Restricted</span>
                                                ) : (
                                                    <span className="text-green-600">Allowed</span>
                                                )}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {affectedStocks.length === 0 ? (
                            <div>
                                <h4 className="mb-3 text-sm font-medium text-gray-700">Affected Stocks</h4>
                                <div className="text-sm text-gray-500">No affected stocks</div>
                            </div>
                        ) : (
                            <Collapsible open={isTableOpen} onOpenChange={setIsTableOpen}>
                                <CollapsibleTrigger className="mb-3 flex items-center gap-2">
                                    <h4 className="text-sm font-medium text-gray-700">Affected Stocks</h4>
                                    <div className="flex h-5 w-5 items-center justify-center rounded border border-gray-300 transition-colors hover:bg-gray-100">
                                        {isTableOpen ? (
                                            <LuChevronUp className="h-3 w-3 text-gray-600" />
                                        ) : (
                                            <LuChevronDown className="h-3 w-3 text-gray-600" />
                                        )}
                                    </div>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <AffectedStocksTable stocks={affectedStocks} />
                                </CollapsibleContent>
                            </Collapsible>
                        )}
                    </div>
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
};
